import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import TextInput from "../../../Shared/TextInput";
import SaveButton from "../../../Shared/Buttons/SaveButton";
import CancelButton from "../../../Shared/Buttons/CancelButton";
import InlineAlert from "../../../Shared/InlineAlert";
import {
  useStudentApi,
  useStateApi,
  useSchoolApi,
  useClassApi,
  useCountryApi
} from "../../../../Hooks";
import DropdownInput from "../../../Shared/DropdownInput/index";
import { isNullOrWhitespace } from "../../../../Utils/TextUtils";
import { emailRegex } from "../../../../Configs";
import { ReactComponent as SpinnerIcon } from "../../../../assets/img/icons/spinner.svg";
import { useTranslation } from "react-i18next";

export default function StudentFormModal({
  isOpen,
  onClose,
  onSuccess,
  student = null,

}) {
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({});
  const [studentForm, setStudentForm] = useState({
    id: "",
    name: "",
    email: "",
    city: null,
    state: null,
    country: null,
    school: null,
    class: null,
  });
  const edit = student !== null;
  const studentApi = useStudentApi();
  const schoolApi = useSchoolApi();
  const classApi = useClassApi();
  const countryApi = useCountryApi();
  const stateApi = useStateApi();
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [buttonsEnabled, setButtonsEnabled] = useState(true);
  const { t } = useTranslation();

  const loadCountries = async () => {
    const getCountries = await countryApi.countryList();
    const countries = getCountries.map((country) => ({
      value: country.id,
      label: country.abbreviation,
    }));
    setCountries(countries);
  };    

  const loadStates = async (country) => {
    if (country === null) {
      setStates([]);
      setCities([]);
      setStudentForm({ ...studentForm, state: null, city: null });
      return;
    }

    const getStates = await stateApi.stateList(country.value);
    const states = getStates.map((state) => ({
      value: state.id,
      label: state.abbreviation,
    }));
    setStates(states);
  };

  const loadCities = async (state) => {
    if (state === null) {
      setCities([]);
      setStudentForm({ ...studentForm, city: null });
      return;
    }

    const getCities = await stateApi.cityList(state.value);
    const cities = getCities.map((city) => ({
      value: city.id,
      label: city.name,
    }));
    setCities(cities);
  };

  const loadSchools = async (city) => {
    if (city === null) {
      setSchools([]);
      setStudentForm({ ...studentForm, school: null });
      return;
    }

    const getSchools = await schoolApi.schoolList(
      0,
      0,
      "",
      city.value,
      studentForm.state.value
    );
    const schools = getSchools.items.map((school) => ({
      value: school.id,
      label: school.name,
    }));
    setSchools(schools);
  };

  const loadClasses = async (school) => {
    if (school === null) {
      setClasses([]);
      setStudentForm({ ...studentForm, class: null });
      return;
    }

    const getClasses = await classApi.classList(
      0,
      0,
      "",
      studentForm.city.value,
      studentForm.state.value,
      school.value
    );
    const classes = getClasses.items.map((_class) => ({
      value: _class.id,
      label: _class.name,
    }));
    setClasses(classes);
  };

  const handleCountryChange = async (e) => {
    clearAlert();
        setStudentForm({
      ...studentForm,
      country: e,
      state: null,
      city: null,
      school: null,
      class: null,
    });
    setStates([]);
    setCities([]);
    setSchools([]);
    setClasses([]);

    loadStates(e);
  };    

  const handleStateChange = async (e) => {
    clearAlert();
    setStudentForm({
      ...studentForm,
      state: e,
      city: null,
      school: null,
      class: null,
    });
    loadCities(e);
  };

  const handleCityChange = async (e) => {
    clearAlert();
    setStudentForm({ ...studentForm, city: e, school: null, class: null });
    loadSchools(e);
  };

  const handleSchoolChange = async (e) => {
    clearAlert();
    setStudentForm({ ...studentForm, school: e, class: null });
    loadClasses(e);
  };

  const handleClassChange = async (e) => {
    clearAlert();
    setStudentForm({ ...studentForm, class: e });
  };

  const clearAlert = () => {
    setAlert({ type: "", message: "" });
  };

  const handleSave = async () => {
    setButtonsEnabled(false);

    setAlert({});
    var alerts = [];

    if (isNullOrWhitespace(studentForm.name)) {
      alerts.push(t("registrationsPage.validation.requiredField", { field: t("registrationsPage.fields.name") }));
    }

    if (isNullOrWhitespace(studentForm.email)) {
      alerts.push(t("registrationsPage.validation.requiredField", { field: t("registrationsPage.fields.email") }));
    } else if (!emailRegex.test(studentForm.email)) {
      alerts.push(t("registrationsPage.validation.invalidEmail"));
    }

    if (studentForm.country === null) {
      alerts.push(t("registrationsPage.validation.requiredField", { field: t("registrationsPage.fields.country") }));
    }

    if (studentForm.state === null) {
      alerts.push(t("registrationsPage.validation.requiredField", { field: t("registrationsPage.fields.state") }));
    }

    if (studentForm.city === null) {
      alerts.push(t("registrationsPage.validation.requiredField", { field: t("registrationsPage.fields.city") }));
    }

    if (studentForm.school === null) {
      alerts.push(t("registrationsPage.validation.requiredField", { field: t("registrationsPage.fields.school") }));
    }

    if (studentForm.class === null) {
      alerts.push(t("registrationsPage.validation.requiredField", { field: t("registrationsPage.fields.class") }));
    }

    if (!alerts.length) {
      setLoading(true);
      await new Promise((r) => setTimeout(r, 500));
      const data = {
        id: studentForm.id,
        name: studentForm.name,
        email: studentForm.email,
        cityId: studentForm.city.value,
        classId: studentForm.class.value,
      };

      const request = edit
        ? await studentApi.updateStudent(data)
        : await studentApi.createStudent(data);

      await handleRequestResult(request);
    } else setAlert({ type: "error", message: alerts.join("<br/>") });

    setButtonsEnabled(true);
    setLoading(false);
  };

  const handleRequestResult = async (request) => {
    if (request.status === 200) {
      setLoading(false);
      setAlert({
        type: "success",
        message: edit
          ? t("registrationsPage.success.studentUpdated")
          : t("registrationsPage.success.studentCreated"),
      });
      await new Promise((r) => setTimeout(r, 1000));
      onSuccess();
      onClose();
    } else setAlert({ type: "error", message: request.response.data});
  };

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);

    if (student !== null) {

      const country = { label: student.countryAbbreviation, value: student.countryId }
      const state = { label: student.stateAbbreviation, value: student.stateId }
      const city = { label: student.cityName, value: student.cityId }
      const school = { label: student.schoolName, value: student.schoolId }
      const _class = { label: student.className, value: student.classId }

      setStudentForm({
        id: student.id,
        name: student.name,
        email: student.email,
        city: city,
        state: state,
        country: country,
        school: school,
        class: _class,
      });
      loadCountries()
      loadStates(country);
      loadCities(state);
      loadSchools(city);
      loadClasses(school);
    } else {
      setStudentForm({
        name: "",
        email: "",
        city: null,
        state: null,
        country: null,
        school: null,
        class: null,
      });
      loadCountries();
      setCities([]);
      setSchools([]);
      setClasses([]);
    }    
    
    setAlert({});
    setLoading(false);
    setButtonsEnabled(true);
  }, [isOpen]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => {return}} >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={`flex min-h-full items-center justify-center p-4 text-center ${
              loading && "cursor-wait"
            }`}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col max-w-md h-[41rem] transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg leading-6 font-bold">
                {edit ? t("registrationsPage.forms.student.titleEdit") : t("registrationsPage.forms.student.titleCreate")}
                </Dialog.Title>

                {loading ? <SpinnerIcon className="m-auto w-24 h-24"/> : <>
                <TextInput
                  id="nome"
                  label={t("registrationsPage.fields.name")}
                  placeholder={t("registrationsPage.forms.student.placeholders.name")}
                  type="text"
                  className="mt-5"
                  value={studentForm.name}
                  onChangeText={(e) =>
                    setStudentForm({ ...studentForm, name: e.target.value })
                  }
                  onClick={clearAlert}
                  error={alert.type === "error"}
                  size="sm"
                />

                <TextInput
                  id="email"
                  label={t("registrationsPage.fields.email")}
                  placeholder={t("registrationsPage.forms.student.placeholders.email")}
                  type="text"
                  className="mt-3"
                  value={studentForm.email}
                  onChangeText={(e) =>
                    setStudentForm({ ...studentForm, email: e.target.value })
                  }
                  onClick={clearAlert}
                  error={alert.type === "error"}
                  size="sm"
                />

                <div className="mt-3 flex flex-row gap-x-3">
                  <DropdownInput
                    label={t("registrationsPage.fields.country")}
                    items={countries}
                    value={studentForm.country}
                    error={alert.type === "error"}
                    inputClassName="w-16"
                    placeholder={t("registrationsPage.placeholders.country")}
                    onChange={handleCountryChange}
                  />
                  
                  <DropdownInput
                    label={t("registrationsPage.fields.stateShort")}
                    items={states}
                    value={studentForm.state}
                    emptyMessage={t("registrationsPage.placeholders.selectCountry")}
                    error={alert.type === "error"}
                    inputClassName="w-16"
                    placeholder={t("registrationsPage.placeholders.state")}
                    onChange={handleStateChange}
                  />
                  <DropdownInput
                    label={t("registrationsPage.fields.city")}
                    items={cities}
                    value={studentForm.city}
                    error={alert.type === "error"}
                    className="grow"
                    placeholder={t("registrationsPage.placeholders.city")}
                    emptyMessage={t("registrationsPage.placeholders.selectState")}
                    onChange={handleCityChange}
                  />
                </div>
                <DropdownInput
                  label={t("registrationsPage.fields.school")}
                  items={schools}
                  value={studentForm.school}
                  error={alert.type === "error"}
                  className="mt-3"
                  placeholder={t("registrationsPage.placeholders.school")}
                  emptyMessage={t("registrationsPage.placeholders.selectCityWithSchools")}
                  onChange={handleSchoolChange}
                />
                <DropdownInput
                  label={t("registrationsPage.fields.class")}
                  items={classes}
                  value={studentForm.class}
                  error={alert.type === "error"}
                  className="mt-3"
                  placeholder={t("registrationsPage.placeholders.class")}
                  emptyMessage={t("registrationsPage.placeholders.selectSchool")}
                  onChange={handleClassChange}
                />
                </>}

                <InlineAlert
                  alert={alert}
                  visible={alert.type === "error" || alert.type === "success"}
                />
                {(!alert.type && !edit) && (
                  <p className="text-center mt-5 font-bold">
                    {t("registrationsPage.forms.student.passwordInfo")}
                  </p>
                )}

                <div className="mt-auto flex flex-row ml-auto gap-x-3">
                  <CancelButton onClick={onClose} enabled={buttonsEnabled} />
                  <SaveButton
                    onClick={handleSave}
                    enabled={buttonsEnabled}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
